import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  useTheme,
  useMediaQuery,
  alpha,
  Stack,
  Chip,
} from '@mui/material';
import {
  TrendingUp,
  Security,
  Speed,
  People,
  Favorite,
  EmojiObjects,
} from '@mui/icons-material';
import { useTheme as useCustomTheme } from '../theme/ThemeProvider';

const AboutUs = () => {
  const theme = useTheme();
  const { darkMode } = useCustomTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const stats = [
    { value: '9', label: 'AI Tools' },
    { value: '100K+', label: 'Users' },
    { value: '1M+', label: 'Analyses' },
    { value: '99.9%', label: 'Uptime' },
  ];

  const values = [
    {
      icon: <EmojiObjects sx={{ fontSize: 48 }} />,
      title: 'Innovation',
      description: 'Constantly pushing boundaries with cutting-edge AI technology to deliver the best text analysis tools.',
    },
    {
      icon: <Speed sx={{ fontSize: 48 }} />,
      title: 'Performance',
      description: 'Lightning-fast processing powered by state-of-the-art machine learning models for instant results.',
    },
    {
      icon: <Security sx={{ fontSize: 48 }} />,
      title: 'Privacy',
      description: 'Your data security is our priority. We never store your text and ensure complete confidentiality.',
    },
    {
      icon: <People sx={{ fontSize: 48 }} />,
      title: 'User-Centric',
      description: 'Designed with you in mind. Simple, intuitive interfaces that anyone can use without training.',
    },
  ];

  const milestones = [
    {
      year: '2023',
      title: 'The Beginning',
      description: 'Launched with 3 core tools to help content creators analyze and optimize their text.',
    },
    {
      year: '2024',
      title: 'Rapid Growth',
      description: 'Expanded to 9 AI-powered tools, serving over 100,000 users worldwide.',
    },
    {
      year: '2025',
      title: 'Innovation Hub',
      description: 'Continuing to innovate with advanced AI models and new features based on user feedback.',
    },
  ];

  return (
    <Box sx={{ overflow: 'hidden' }}>
      {/* Hero Section */}
      <Box
        sx={{
          position: 'relative',
          py: { xs: 8, md: 12 },
          background: darkMode
            ? 'linear-gradient(135deg, #1a202c 0%, #2d3748 100%)'
            : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'radial-gradient(circle at 20% 50%, rgba(99, 102, 241, 0.3) 0%, transparent 50%)',
            opacity: darkMode ? 0.3 : 0.5,
          },
        }}
      >
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Box sx={{ textAlign: 'center', maxWidth: 900, mx: 'auto' }}>
            <Chip
              label="About TextSimpler"
              sx={{
                mb: 3,
                background: 'rgba(255, 255, 255, 0.2)',
                color: 'white',
                fontWeight: 600,
                backdropFilter: 'blur(10px)',
              }}
            />
            <Typography
              variant={isMobile ? 'h3' : 'h2'}
              component="h1"
              sx={{
                fontWeight: 800,
                mb: 3,
                fontSize: { xs: '2.5rem', md: '3.5rem' },
              }}
            >
              Transforming Text Analysis with AI
            </Typography>
            <Typography
              variant="h6"
              sx={{
                mb: 4,
                opacity: 0.95,
                lineHeight: 1.8,
                fontSize: { xs: '1.1rem', md: '1.25rem' },
              }}
            >
              We're on a mission to make advanced text analysis accessible to everyone.
              Our suite of AI-powered tools helps creators, businesses, and individuals
              unlock the full potential of their content.
            </Typography>

            {/* Stats */}
            <Grid container spacing={3} sx={{ mt: 4 }}>
              {stats.map((stat, index) => (
                <Grid item xs={6} sm={3} key={index}>
                  <Box>
                    <Typography
                      variant="h3"
                      sx={{
                        fontWeight: 700,
                        mb: 1,
                      }}
                    >
                      {stat.value}
                    </Typography>
                    <Typography variant="body1" sx={{ opacity: 0.9 }}>
                      {stat.label}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>
        </Container>
      </Box>



      {/* Core Values Section */}
      <Container maxWidth="lg" sx={{ py: 10 }}>
        <Box sx={{ textAlign: 'center', mb: 8 }}>
          <Typography
            variant="h3"
            sx={{
              fontWeight: 700,
              mb: 2,
              background: 'linear-gradient(135deg, #6366f1 0%, #06b6d4 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            Our Core Values
          </Typography>
          <Typography variant="h6" sx={{ color: 'text.secondary', maxWidth: 600, mx: 'auto' }}>
            The principles that guide everything we do
          </Typography>
        </Box>

        <Grid container spacing={4}>
          {values.map((value, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <Card
                sx={{
                  height: '100%',
                  background: darkMode
                    ? alpha('#6366f1', 0.05)
                    : 'white',
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                  borderRadius: 3,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: `0 12px 24px ${alpha(theme.palette.primary.main, 0.15)}`,
                    borderColor: theme.palette.primary.main,
                  },
                }}
              >
                <CardContent sx={{ p: 3, textAlign: 'center' }}>
                  <Box
                    sx={{
                      color: 'primary.main',
                      mb: 2,
                    }}
                  >
                    {value.icon}
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                    {value.title}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.7 }}>
                    {value.description}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Timeline Section */}
      <Box
        sx={{
          py: 10,
          background: darkMode
            ? 'linear-gradient(135deg, #1a202c 0%, #2d3748 100%)'
            : 'linear-gradient(135deg, #f7fafc 0%, #e2e8f0 100%)',
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography
              variant="h3"
              sx={{
                fontWeight: 700,
                mb: 2,
                background: 'linear-gradient(135deg, #6366f1 0%, #06b6d4 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              Our Journey
            </Typography>
            <Typography variant="h6" sx={{ color: 'text.secondary' }}>
              Key milestones in our growth
            </Typography>
          </Box>

          <Stack spacing={4}>
            {milestones.map((milestone, index) => (
              <Card
                key={index}
                sx={{
                  background: darkMode
                    ? alpha('#6366f1', 0.05)
                    : 'white',
                  border: `2px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                  borderRadius: 3,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    borderColor: theme.palette.primary.main,
                    boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.15)}`,
                  },
                }}
              >
                <CardContent sx={{ p: 4 }}>
                  <Grid container spacing={3} alignItems="center">
                    <Grid item xs={12} sm={3}>
                      <Box
                        sx={{
                          width: { xs: '100%', sm: 100 },
                          height: 100,
                          borderRadius: 2,
                          background: 'linear-gradient(135deg, #6366f1 0%, #06b6d4 100%)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mx: { xs: 'auto', sm: 0 },
                        }}
                      >
                        <Typography variant="h4" sx={{ fontWeight: 700, color: 'white' }}>
                          {milestone.year}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={9}>
                      <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
                        {milestone.title}
                      </Typography>
                      <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.8 }}>
                        {milestone.description}
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            ))}
          </Stack>
        </Container>
      </Box>

      {/* CTA Section */}
      <Container maxWidth="lg" sx={{ py: 10 }}>
        <Box
          sx={{
            textAlign: 'center',
            p: { xs: 4, md: 8 },
            borderRadius: 4,
            background: 'linear-gradient(135deg, #6366f1 0%, #06b6d4 100%)',
            color: 'white',
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'radial-gradient(circle at 80% 20%, rgba(255, 255, 255, 0.2) 0%, transparent 50%)',
            },
          }}
        >
          <Box sx={{ position: 'relative', zIndex: 1 }}>
            <Favorite sx={{ fontSize: 60, mb: 3, opacity: 0.9 }} />
            <Typography variant="h3" sx={{ fontWeight: 700, mb: 2 }}>
              Join Thousands of Happy Users
            </Typography>
            <Typography variant="h6" sx={{ mb: 4, opacity: 0.95, maxWidth: 600, mx: 'auto' }}>
              Start using our AI-powered tools today and experience the difference
            </Typography>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default AboutUs;
